import { isPlatformBrowser } from '@angular/common';
import {
  AfterViewInit,
  ChangeDetectorRef,
  Component,
  ElementRef,
  OnDestroy,
  PLATFORM_ID,
  inject,
} from '@angular/core';
import { Meta } from '@angular/platform-browser';
import { ActivatedRoute, Router } from '@angular/router';
import { environment } from '../../../../../environments/environment';
import { HumanVerifyService } from '../../../../services/human-verify.service';

const TURNSTILE_SCRIPT_ID = 'cf-turnstile-api';
const TURNSTILE_SRC =
  'https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit';

declare global {
  interface Window {
    turnstile?: {
      render: (
        el: HTMLElement,
        options: {
          sitekey: string;
          callback?: (token: string) => void;
          'error-callback'?: (errorCode?: string) => void;
          'expired-callback'?: () => void;
          theme?: 'light' | 'dark' | 'auto';
          size?: 'normal' | 'flexible' | 'compact';
          appearance?: 'always' | 'execute' | 'interaction-only';
        }
      ) => string;
      remove: (widgetId: string) => void;
      reset: (widgetId?: string) => void;
    };
  }
}

@Component({
  selector: 'app-human-verify',
  standalone: true,
  imports: [],
  templateUrl: './human-verify.component.html',
  styleUrl: './human-verify.component.scss',
  host: { ngSkipHydration: 'true' },
})
export class HumanVerifyComponent implements AfterViewInit, OnDestroy {
  private readonly platformId = inject(PLATFORM_ID);
  private readonly router = inject(Router);
  private readonly route = inject(ActivatedRoute);
  private readonly humanVerify = inject(HumanVerifyService);
  private readonly hostEl = inject(ElementRef<HTMLElement>);
  private readonly cdr = inject(ChangeDetectorRef);
  private readonly meta = inject(Meta);

  readonly siteKey = environment.turnstile?.siteKey || '';

  // Plain fields (not signals): signal writes were re-rendering the view and
  // wiping the Turnstile iframe that Cloudflare injects into the host.
  statusText = 'Checking that you are human…';
  errorText = '';
  busy = false;

  private widgetId: string | null = null;
  private destroyed = false;
  private pollTimer: ReturnType<typeof setInterval> | null = null;
  private startTimer: ReturnType<typeof setTimeout> | null = null;
  private viewDetached = false;

  constructor() {
    // Keep verify gate out of search indexes (SSR + browser).
    this.meta.updateTag({ name: 'robots', content: 'noindex, nofollow' });
  }

  ngAfterViewInit(): void {
    if (!isPlatformBrowser(this.platformId)) {
      return;
    }
    // Skip-hydration recreates the view; wait a tick for the final DOM.
    this.startTimer = setTimeout(() => this.bootstrapWidget(), 100);
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    // Restore default site robots when leaving verify page.
    this.meta.updateTag({ name: 'robots', content: 'index, follow' });
    if (this.startTimer) {
      clearTimeout(this.startTimer);
      this.startTimer = null;
    }
    if (this.pollTimer) {
      clearInterval(this.pollTimer);
      this.pollTimer = null;
    }
    if (this.widgetId && window.turnstile) {
      try {
        window.turnstile.remove(this.widgetId);
      } catch {
        /* ignore */
      }
    }
    if (this.viewDetached) {
      this.cdr.reattach();
      this.viewDetached = false;
    }
  }

  private bootstrapWidget(): void {
    if (this.destroyed) {
      return;
    }

    if (!environment.turnstile?.enabled) {
      this.humanVerify.markVerified();
      this.navigateNext();
      return;
    }

    if (this.humanVerify.isVerified()) {
      this.humanVerify.markVerified();
      this.navigateNext();
      return;
    }

    if (!this.siteKey || this.siteKey.startsWith('YOUR_')) {
      this.errorText =
        'Turnstile Site Key missing. Add it in environment.turnstile.siteKey.';
      this.statusText = 'Configuration required';
      this.cdr.detectChanges();
      return;
    }

    void this.ensureScript()
      .then(() => this.mountWidget())
      .catch((err: unknown) => {
        this.statusText = 'Widget did not load';
        this.errorText =
          err instanceof Error
            ? err.message
            : 'Could not load Cloudflare Turnstile. Check CSP / network.';
        this.cdr.detectChanges();
      });
  }

  private getSlot(): HTMLElement | null {
    const root = this.hostEl.nativeElement as HTMLElement;
    const slot = root.querySelector('#cf-turnstile-slot');
    return slot instanceof HTMLElement && slot.isConnected ? slot : null;
  }

  private findExistingScript(): HTMLScriptElement | null {
    const byId = document.getElementById(TURNSTILE_SCRIPT_ID);
    if (byId instanceof HTMLScriptElement) {
      return byId;
    }
    return (
      Array.from(document.scripts).find((s) =>
        (s.src || '').includes('challenges.cloudflare.com/turnstile')
      ) || null
    );
  }

  private ensureScript(): Promise<void> {
    if (window.turnstile?.render) {
      return Promise.resolve();
    }

    const existing = this.findExistingScript();
    if (existing) {
      existing.removeAttribute('async');
      existing.removeAttribute('defer');
      existing.async = false;
      existing.defer = false;

      return new Promise((resolve, reject) => {
        if (window.turnstile?.render) {
          resolve();
          return;
        }
        existing.addEventListener('load', () => resolve(), { once: true });
        existing.addEventListener(
          'error',
          () => reject(new Error('Turnstile script failed to load')),
          { once: true }
        );
        setTimeout(() => {
          if (window.turnstile?.render) {
            resolve();
          }
        }, 50);
      });
    }

    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.id = TURNSTILE_SCRIPT_ID;
      script.src = TURNSTILE_SRC;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Turnstile script failed to load'));
      document.head.appendChild(script);
    });
  }

  private mountWidget(): void {
    if (this.destroyed) {
      return;
    }

    const tryRender = (): boolean => {
      if (this.destroyed || this.widgetId) {
        return !!this.widgetId;
      }
      if (!window.turnstile?.render) {
        return false;
      }

      const host = this.getSlot();
      if (!host) {
        return false;
      }

      // Detach CD so later status updates do not recreate this view and wipe the iframe.
      if (!this.viewDetached) {
        this.cdr.detach();
        this.viewDetached = true;
      }

      host.innerHTML = '';

      try {
        this.widgetId = window.turnstile.render(host, {
          sitekey: this.siteKey,
          theme: 'light',
          size: 'normal',
          appearance: 'always',
          callback: (token) => this.onToken(token),
          'error-callback': (errorCode) => {
            this.widgetId = null;
            this.errorText = errorCode
              ? `Verification widget error (${errorCode}). Add this hostname in Cloudflare Turnstile → Hostname Management (e.g. portalstg.creativestall.com).`
              : 'Verification widget failed to load. Please refresh.';
            this.statusText = 'Something went wrong';
            this.refreshView();
          },
          'expired-callback': () => {
            this.errorText = 'Verification expired. Please complete the check again.';
            this.statusText = 'Please try again';
            if (this.widgetId && window.turnstile) {
              window.turnstile.reset(this.widgetId);
            }
            this.refreshView();
          },
        });

        this.statusText = 'Complete the check below to continue';
        this.errorText = '';
        // Manually patch the status text without reattaching CD (avoids wiping iframe).
        const copy = (this.hostEl.nativeElement as HTMLElement).querySelector('.hv-copy');
        if (copy) {
          copy.textContent = this.statusText;
        }
        return true;
      } catch (err) {
        this.errorText =
          err instanceof Error ? err.message : 'Failed to render Turnstile widget.';
        this.statusText = 'Something went wrong';
        this.refreshView();
        return false;
      }
    };

    if (!tryRender()) {
      this.startPoll(tryRender);
    }
  }

  private startPoll(tryRender: () => boolean): void {
    let attempts = 0;
    this.pollTimer = setInterval(() => {
      attempts += 1;
      if (tryRender() || attempts > 60) {
        if (this.pollTimer) {
          clearInterval(this.pollTimer);
          this.pollTimer = null;
        }
        if (attempts > 60 && !this.widgetId) {
          this.errorText =
            'Could not load Cloudflare Turnstile. Allow challenges.cloudflare.com, then refresh.';
          this.statusText = 'Widget did not load';
          this.refreshView();
        }
      }
    }, 250);
  }

  private refreshView(): void {
    if (this.viewDetached) {
      this.cdr.reattach();
      this.viewDetached = false;
    }
    this.cdr.detectChanges();
    // Re-detach after painting error/busy text so a later reset can remount cleanly.
    if (this.widgetId) {
      this.cdr.detach();
      this.viewDetached = true;
    }
  }

  private onToken(token: string): void {
    if (this.busy) {
      return;
    }
    this.busy = true;
    this.errorText = '';
    this.statusText = 'Verifying…';
    this.refreshView();

    this.humanVerify.verifyToken(token).subscribe((ok) => {
      this.busy = false;
      if (!ok) {
        this.errorText =
          'Verification failed. Confirm API SecretKey matches this Site Key, then try again.';
        this.statusText = 'Please try again';
        if (this.widgetId && window.turnstile) {
          window.turnstile.reset(this.widgetId);
        }
        this.refreshView();
        return;
      }
      this.statusText = 'Success! Opening Creative Stall…';
      this.refreshView();
      this.navigateNext();
    });
  }

  private navigateNext(): void {
    const returnUrl = this.route.snapshot.queryParamMap.get('returnUrl') || '/';
    void this.router.navigateByUrl(returnUrl.startsWith('/') ? returnUrl : '/');
  }
}
